<template>
	<div class="sticky top-0 z-4" :class="{ 'pt-4': !isXS }">
		<div
			v-if="isXS"
			class="fixed bottom-0 width-full mobile-container bg-neutral-50 d-flex align-center justify-space-evenly border-t-s border-neutral-100"
		>
			<HeaderButton
				icon="home"
				:text="$t('web.header.navigation.home')"
				:is-x-s="isXS"
				:is-active="isActive('home')"
				@click="navigateToHome"
			/>
			<HeaderButton
				icon="search"
				:text="$t('web.header.navigation.explore')"
				:is-x-s="isXS"
				:is-active="isActive('explore')"
				@click="ExploreNavigation.go()"
			/>
			<PButton
				class="rounded-3"
				size="xl"
				icon="add"
				:only-icon="isXS"
				@click="TripNavigation.toCreateTrip()"
			></PButton>
			<HeaderButton
				icon="luggage"
				:text="$t('web.header.navigation.trips')"
				:is-x-s="isXS"
				:is-active="isActive('my-trips')"
				@click="navigateToMyTrips"
			/>
			<div class="d-flex flex-column align-center mt-3">
				<Avatar :src="profile?.avatar" width="24px" height="24px" @click="navigateToProfile" />
				<span class="text-label-l text-neutral-800">{{ $t('web.header.user_menu.passport') }}</span>
			</div>
		</div>
		<Content v-else :is-mobile="isXS">
			<div class="relative height-full width-full" :style="{ height: '56px' }">
				<div
					class="header-desktop-background header-border pa-4 rounded-4 absolute height-full width-full border-s"
				></div>
				<div class="d-flex align-center py-2 px-4 rounded-4 z-1 inherit">
					<HeaderLogo
						class="side-column"
						:is-x-s="isXS"
						:use-green-logo="invertLogo"
						@click="navigateToHome"
					/>
					<div class="middle-column d-flex gap-3 justify-center">
						<HeaderButton
							icon="home"
							:text="$t('web.header.navigation.home')"
							:is-active="isActive('home')"
							@click="navigateToHome"
						/>
						<HeaderButton
							icon="search"
							:text="$t('web.header.navigation.explore')"
							:is-active="isActive('explore')"
							@click="ExploreNavigation.go()"
						/>
						<HeaderButton
							icon="luggage"
							:text="$t('web.header.navigation.trips')"
							:is-active="isActive('my-trips')"
							@click="navigateToMyTrips"
						/>
					</div>
					<div class="side-column d-flex gap-3 align-center justify-end">
						<PButton icon="add" @click="TripNavigation.toCreateTrip()">
							{{ $t('web.header.navigation.primary_button') }}
						</PButton>
						<HeaderNotifications :user-id="profile?.id" :is-x-s="isXS" />
						<HeaderProfileChip :profile="profile" :is-x-s="isXS" />
					</div>
				</div>
			</div>
		</Content>
	</div>
</template>

<script setup lang="ts">
	import { storeToRefs } from 'pinia'
	import { AuthActions } from '~/actions/auth/action'
	import CookieService from '~/services/cookies'
	import { ExploreNavigation } from '~/services/navigation/explore'
	import { HomeNavigation } from '~/services/navigation/home'
	import { MyTripsNavigation } from '~/services/navigation/myTrips'
	import { ProfileNavigation } from '~/services/navigation/profileNavigation'
	import { TripNavigation } from '~/services/navigation/trip'
	import { RouteService } from '~/services/route/service'
	import { useDeviceStore } from '~/store/device'
	import { useProfileStore } from '~/store/profile'

	const deviceStore = useDeviceStore()
	const { isXS } = storeToRefs(deviceStore)
	const route = useRoute()

	const profileStore = useProfileStore()
	const { profile } = storeToRefs(profileStore)

	const authRequireDialog = AuthActions.createRegisterRequireDialog(isXS.value)

	interface Props {
		invertLogo?: boolean
		needLoginToCreateTrip?: boolean
	}

	defineProps<Props>()

	const isActive = (path: string): boolean => {
		const routeName = route.name?.toString()
		if (!routeName) return false
		const normalizedName = RouteService.getNormalizedRouteName(routeName)
		return normalizedName === path
	}

	const navigateToHome = () => {
		if (!hasAccessToRoute(HomeNavigation.route)) return
		HomeNavigation.toHome()
	}

	const navigateToMyTrips = () => {
		if (!hasAccessToRoute(MyTripsNavigation.route)) return
		MyTripsNavigation.go()
	}

	const navigateToProfile = () => {
		if (!hasAccessToRoute(ProfileNavigation.route)) return
		ProfileNavigation.toProfile()
	}

	const hasAccessToRoute = (route: string): boolean => {
		if (!isAuthenticated()) {
			authRequireDialog.open({
				routeToGoAfterLogin: route,
			})
			return false
		}
		return true
	}

	const isAuthenticated = (): boolean => {
		const authCookie = CookieService.getCookie('authentication-cookie')
		const isAuthenticated = authCookie?.auth?.accessToken
		return Boolean(isAuthenticated)
	}
</script>

<style lang="scss" scoped>
	.header-desktop-background {
		backdrop-filter: blur(40px);
		background: #f6f6f64d;
	}
	.header-border {
		border-color: #ffffff33;
	}
	.mobile-container {
		height: 98px;
		backdrop-filter: blur(40px);
		background: #f6f6f64d;
	}
	.middle-column {
		flex-grow: 1;
	}
	.side-column {
		flex-grow: 0;
		flex-basis: 0;
		flex-grow: 1;
	}
</style>
